import useSWR from 'swr';
import { useMemo } from 'react';
import { useAppContext } from 'src/context/app';

import axiosInstance, { endpoints, fetcher } from 'src/lib/axios';
import { IBanktransaktion } from "../types/nya/ekonomi/bank/BankTyper";

// ----------------------------------------------------------------------

const swrOptions = {
  revalidateIfStale: false,
  revalidateOnFocus: false,
  revalidateOnReconnect: false,
};

// ----------------------------------------------------------------------

type TransaktionerRespons = {
  orgId: string;
  transaktioner: IBanktransaktion[];
};

type TransaktionsFilter = {
  inkop?: boolean;
  ickeBokfördaTransaktioner?: boolean;
  troligenLön?: boolean;
};


export function HämtaTransaktioner(filter: TransaktionsFilter) {
  const { organisation } = useAppContext();
  const orgId = organisation?.id;
  const params = new URLSearchParams();
  if (filter.troligenLön) {
    params.append('troligenLön', 'true');
  }
  if (filter.inkop) {
    params.append('inkop', 'true');
  }
  let url = orgId ? endpoints.bank.banktransaktioner.root + "/" + orgId : null;
  if (url && params.size > 0) {
    url = url + '?' + params.toString();
  }
  const { data, isLoading, error, isValidating } = useSWR<TransaktionerRespons>(url, fetcher, swrOptions);

  const memoizedValue = useMemo(
    () => ({
      transaktioner: data?.transaktioner || [],
      transaktionerLoading: isLoading,
      transaktionerError: error,
      transaktionerValidating: isValidating,
      transaktionerEmpty: !isLoading && data?.transaktioner?.length === 0,
    }),
    [data, error, isLoading, isValidating]
  );

  return memoizedValue;
}


export function useGetTransaktioner(filtreraUtBokfördaTransaktioner: boolean) {
  const { organisation } = useAppContext();
  const orgId = organisation?.id;
  const url = orgId ? endpoints.bank.banktransaktioner.root + "/" + orgId : null;

  const { data, isLoading, error, isValidating, mutate } = useSWR<TransaktionerRespons>(url, fetcher, swrOptions);

  const memoizedValue = useMemo(
    () => ({
      transaktioner: data?.transaktioner || [],
      transaktionerLoading: isLoading,
      transaktionerError: error,
      transaktionerValidating: isValidating,
      transaktionerEmpty: !isLoading && data?.transaktioner?.length === 0,
      mutate,
    }),
    [data, error, isLoading, isValidating, mutate]
  );

  return memoizedValue;
}

// ----------------------------------------------------------------------
type TransaktionRespons = {
  orgId: string;
  transaktion: IBanktransaktion;
};


export function useGetTransaktion(id: string) {
  const { organisation } = useAppContext();
  const orgId = organisation?.id;
  const url = id && orgId ? endpoints.bank.banktransaktioner.root + "/" + orgId + '/' + id : null;

  const { data, isLoading, error, isValidating } = useSWR<TransaktionRespons>(url, fetcher, swrOptions);

  const memoizedValue = useMemo(
    () => ({
      transaktion: data?.transaktion,
      transaktionLoading: isLoading,
      transaktionError: error,
      transaktionValidating: isValidating,
    }),
    [data?.transaktion, error, isLoading, isValidating]
  );

  return memoizedValue;
}

export async function skapaInköpFrånBanktransaktioner(orgId: string) {
  const url = endpoints.bank.banktransaktioner.skapaInköp(orgId);
  try {
    const response = await axiosInstance.get(url);
    return response.data;
  } catch (error: any) {
    console.error('[skapaInköpFrånBanktransaktioner] Error:', error?.response?.status, error?.response?.data || error?.message);
    throw error;
  }
}

export function useHämtaBanktransaktionerAttBokföra(startDate?: string, endDate?: string) {
  const { organisation } = useAppContext();
  const orgId = organisation?.id;
  const params = new URLSearchParams();
  if (startDate) params.append('startDate', startDate);
  if (endDate) params.append('endDate', endDate);
  const queryString = params.toString();
  const url = orgId
    ? `${endpoints.bank.banktransaktioner.attBokföra(orgId)}${queryString ? `?${queryString}` : ''}`
    : null;

  const { data, isLoading, error, isValidating, mutate } = useSWR<TransaktionerRespons>(url, fetcher, swrOptions);

  const memoizedValue = useMemo(
    () => ({
      transaktioner: data?.transaktioner || [],
      transaktionerLoading: isLoading,
      transaktionerError: error,
      transaktionerValidating: isValidating,
      transaktionerEmpty: !isLoading && data?.transaktioner?.length === 0,
      mutate,
    }),
    [data, error, isLoading, isValidating, mutate]
  );

  return memoizedValue;
}

export function useGetTransaktionerFörBokföringskontonummer(bokföringskontonummer: number) {
  const { organisation } = useAppContext();
  const orgId = organisation?.id;
  const url = orgId ? endpoints.bank.banktransaktioner.förBokföringskontonummer(orgId, bokföringskontonummer) : null;

  const { data, isLoading, error, isValidating, mutate } = useSWR<TransaktionerRespons>(url, fetcher, swrOptions);

  const memoizedValue = useMemo(
    () => ({
      transaktioner: data?.transaktioner || [],
      transaktionerLoading: isLoading,
      transaktionerError: error,
      transaktionerValidating: isValidating,
      transaktionerEmpty: !isLoading && data?.transaktioner?.length === 0,
      mutate,
    }),
    [data, error, isLoading, isValidating, mutate]
  );

  return memoizedValue;
}
